/**
 * Patron Points — PLAYERS. Read only.
 *
 * Searchable, sortable, paginated table in the Review Shield results style,
 * with a filter bar whose "who matches" count updates on every change. Export
 * writes the filtered rows, nothing more.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { EmailChip, PrizeChip, VisitChip } from './PatronChips';

const num = (n) => Number(n || 0).toLocaleString();
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const BLANK = {
    q: '', night: '', league: '', team: '', star_player: '', last_visit: '',
    email_status: '', place: '', prize_status: '', points_min: '', points_max: '',
};

const Picker = ({ label, value, onChange, options, testid, t }) => (
    <label className="block">
        <span className="block text-[11px] font-semibold text-slate-500 mb-1">{label}</span>
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="h-9 min-w-[8.5rem] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm">
            <option value="">{t('patronCommand.filters.all')}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    </label>
);

export const PatronPlayers = ({ orgId, onOpenPlayer }) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState(BLANK);
    const [sort, setSort] = useState('points');
    const [direction, setDirection] = useState('desc');
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);

    const params = useCallback(() => {
        const out = { organization_id: orgId, sort, direction, page, page_size: 25 };
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== '' && v !== null) out[k] = v;
        });
        return out;
    }, [orgId, filters, sort, direction, page]);

    useEffect(() => {
        if (!orgId) return;
        (async () => {
            const res = await api.get('/patron/insights/players', { params: params() });
            setData(res.data);
        })();
    }, [orgId, params]);

    const set = (key, value) => { setPage(1); setFilters((f) => ({ ...f, [key]: value })); };

    const head = (key, label) => (
        <TableHead>
            <button onClick={() => {
                setDirection(sort === key && direction === 'desc' ? 'asc' : 'desc');
                setSort(key);
            }} data-testid={`patron-sort-${key}`} className="font-semibold">
                {label}{sort === key ? (direction === 'desc' ? ' ↓' : ' ↑') : ''}
            </button>
        </TableHead>
    );

    const exportCsv = async () => {
        try {
            const res = await api.get('/patron/insights/players.csv',
                { params: params(), responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url; a.download = 'patron-players.csv'; a.click();
            URL.revokeObjectURL(url);
        } catch (_e) { toast.error('Export failed'); }
    };

    if (!data) return <p className="text-sm text-slate-500" data-testid="patron-players-loading">…</p>;

    const o = data.options || {};
    const pages = Math.max(1, Math.ceil(data.matched / data.page_size));

    return (
        <div className="space-y-4" data-testid="patron-players">
            <Card>
                <CardContent className="pt-5 space-y-3">
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="block grow min-w-[12rem]">
                            <span className="block text-[11px] font-semibold text-slate-500 mb-1">
                                {t('patronCommand.players.search')}
                            </span>
                            <span className="relative block">
                                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input value={filters.q} onChange={(e) => set('q', e.target.value)}
                                       data-testid="patron-players-search" className="pl-8 h-9" />
                            </span>
                        </label>
                        <Picker t={t} label={t('patronCommand.filters.night')} value={filters.night}
                                onChange={(v) => set('night', v)} testid="patron-filter-night"
                                options={(o.nights || []).map((n) => ({ value: n.night, label: `${n.night} · ${n.label}` }))} />
                        <Picker t={t} label={t('patronCommand.filters.league')} value={filters.league}
                                onChange={(v) => set('league', v)} testid="patron-filter-league"
                                options={(o.leagues || []).map((l) => ({ value: l, label: l.toUpperCase() }))} />
                        <Picker t={t} label={t('patronCommand.filters.star')} value={filters.star_player}
                                onChange={(v) => set('star_player', v)} testid="patron-filter-star"
                                options={(o.star_players || []).map((p) => ({ value: p.id, label: p.name }))} />
                        <Picker t={t} label={t('patronCommand.filters.team')} value={filters.team}
                                onChange={(v) => set('team', v)} testid="patron-filter-team"
                                options={(o.teams || []).map((x) => ({ value: x, label: x }))} />
                        <Picker t={t} label={t('patronCommand.filters.lastVisit')} value={filters.last_visit}
                                onChange={(v) => set('last_visit', v)} testid="patron-filter-last-visit"
                                options={['recent', 'fading', 'cold', 'never'].map((k) => ({
                                    value: k, label: t(`patronCommand.recency.${k}`) }))} />
                        <Picker t={t} label={t('patronCommand.filters.emailStatus')} value={filters.email_status}
                                onChange={(v) => set('email_status', v)} testid="patron-filter-email"
                                options={['accepted', 'none', 'unsubscribed'].map((k) => ({
                                    value: k, label: t(`patronCommand.emailStatus.${k}`) }))} />
                        <Picker t={t} label={t('patronCommand.filters.place')} value={filters.place}
                                onChange={(v) => set('place', v)} testid="patron-filter-place"
                                options={['room', 'remote'].map((k) => ({
                                    value: k, label: t(`patronCommand.place.${k}`) }))} />
                        <Picker t={t} label={t('patronCommand.filters.prize')} value={filters.prize_status}
                                onChange={(v) => set('prize_status', v)} testid="patron-filter-prize"
                                options={['active', 'redeemed', 'expired', 'none'].map((k) => ({
                                    value: k, label: t(`patronCommand.prize.${k}`) }))} />
                        <label className="block">
                            <span className="block text-[11px] font-semibold text-slate-500 mb-1">
                                {t('patronCommand.filters.points')}
                            </span>
                            <span className="flex items-center gap-1">
                                <Input type="number" value={filters.points_min} className="h-9 w-20"
                                       placeholder={t('patronCommand.filters.min')}
                                       onChange={(e) => set('points_min', e.target.value)}
                                       data-testid="patron-filter-points-min" />
                                <Input type="number" value={filters.points_max} className="h-9 w-20"
                                       placeholder={t('patronCommand.filters.max')}
                                       onChange={(e) => set('points_max', e.target.value)}
                                       data-testid="patron-filter-points-max" />
                            </span>
                        </label>
                        <Button variant="ghost" size="sm" className="h-9"
                                onClick={() => { setFilters(BLANK); setPage(1); }}
                                data-testid="patron-filters-clear">
                            <X size={14} className="mr-1" />{t('patronCommand.players.clear')}
                        </Button>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold" data-testid="patron-players-count">
                            {t('patronCommand.players.matches',
                               { count: data.matched, total: data.total })}
                        </p>
                        <Button variant="outline" size="sm" onClick={exportCsv}
                                data-testid="patron-players-export">
                            <Download size={14} className="mr-1" />{t('patronCommand.players.export')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="pt-5">
                    {data.total === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="patron-players-none">
                            {t('patronCommand.players.none')}
                        </p>
                    ) : data.rows.length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="patron-players-empty">
                            {t('patronCommand.players.empty')}
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="patron-players-table">
                                <TableHeader>
                                    <TableRow>
                                        {head('screen_name', t('patronCommand.columns.screenName'))}
                                        {head('email', t('patronCommand.columns.email'))}
                                        {head('points', t('patronCommand.columns.points'))}
                                        {head('visits', t('patronCommand.columns.visits'))}
                                        {head('last_visit', t('patronCommand.columns.lastVisit'))}
                                        {head('goals_credited', t('patronCommand.columns.goals'))}
                                        {head('star_player', t('patronCommand.columns.star'))}
                                        {head('prizes', t('patronCommand.columns.prizes'))}
                                        {head('joined', t('patronCommand.columns.joined'))}
                                        {head('language', t('patronCommand.columns.language'))}
                                        {head('email_status', t('patronCommand.columns.emailStatus'))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.rows.map((r) => (
                                        <TableRow key={r.id} onClick={() => onOpenPlayer(r.id)}
                                                  className="cursor-pointer"
                                                  data-testid={`patron-player-row-${r.id}`}>
                                            <TableCell className="font-medium">{r.screen_name || '—'}</TableCell>
                                            <TableCell className="text-xs">{r.email}</TableCell>
                                            <TableCell className="text-right tabular-nums font-semibold">{num(r.points)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{r.visits}</TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">
                                                <span className="block mb-1">{day(r.last_visit)}</span>
                                                <VisitChip recency={r.visit_recency} t={t}
                                                           testId={`patron-visit-chip-${r.id}`} />
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">{r.goals_credited}</TableCell>
                                            <TableCell className="text-xs">{r.star_player || '—'}</TableCell>
                                            <TableCell className="text-xs">
                                                <PrizeChip state={r.prize_status} t={t}
                                                           testId={`patron-prize-chip-${r.id}`} />
                                            </TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">{day(r.joined)}</TableCell>
                                            <TableCell className="text-xs uppercase">{r.language || '—'}</TableCell>
                                            <TableCell>
                                                <EmailChip status={r.email_status} t={t}
                                                           testId={`patron-email-chip-${r.id}`} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    {pages > 1 && (
                        <div className="flex items-center justify-between pt-4">
                            <Button variant="outline" size="sm" disabled={page <= 1}
                                    onClick={() => setPage((p) => p - 1)}
                                    data-testid="patron-players-prev">
                                {t('patronCommand.players.prev')}
                            </Button>
                            <span className="text-xs text-slate-500" data-testid="patron-players-page">
                                {t('patronCommand.players.page', { page })} / {pages}
                            </span>
                            <Button variant="outline" size="sm" disabled={page >= pages}
                                    onClick={() => setPage((p) => p + 1)}
                                    data-testid="patron-players-next">
                                {t('patronCommand.players.next')}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
